import React from 'react';

import { IconButton } from '@chakra-ui/react';
import { useTheme } from '@chakra-ui/system';
import { CircularProgress } from '@chakra-ui/progress';
import { Box, Center, Flex, Text } from '@chakra-ui/layout';

import { GrNext, GrPrevious } from 'react-icons/gr';

import {
  TABLE_FRAME_COLOR,
  TABLE_GRID_COLOR,
} from 'components/modules/DataTable/tableBorders';

import { CustomTableProps } from './CustomTable.props';

const LIMITS_PER_PAGE = 10;

/**
 * Applied through Chakra's `sx` (NOT the raw emotion `css` prop) so the color
 * tokens resolve — under `css` they reached the browser as the literal string
 * "primary.400", leaving an invisible scrollbar thumb over a cropped table.
 */
const scrollbarSx = {
  // Firefox ignores ::-webkit-scrollbar; these are its equivalent knobs.
  scrollbarWidth: 'thin' as const,
  scrollbarColor: 'var(--chakra-colors-primary-400) transparent',
  '&::-webkit-scrollbar': {
    width: '10px',
    height: '10px',
  },
  '&::-webkit-scrollbar-track': {
    bg: 'primary.900',
    borderRadius: '24px',
  },
  '&::-webkit-scrollbar-thumb': {
    bg: 'primary.400',
    borderRadius: '24px',
  },
};

/**
 * Header, sub-header, rows and footer are all laid out by the same rules, so a
 * cell resolves to the same width in every one of them. Previously each band
 * sized itself independently and only lined up by coincidence.
 *
 * Pages render a row's cells in one of two shapes: as the band's direct
 * children, or wrapped in a single element carrying the row's onClick. The
 * wrapper is dissolved with `display: contents` so both shapes resolve against
 * the same box — it keeps its click handler, since only its layout box goes
 * away. `:only-child` is exactly the wrapper signature: a fragment yields two
 * or more children, and the few rows that pair a wrapper with a sibling action
 * column are left alone.
 *
 * Spacing is per-cell padding rather than a `gap` for two reasons. Dissolving
 * the wrapper removes it from layout but not from selector matching, so cells
 * have to be addressed at both depths; and padding sits inside the cell box, so
 * the tables whose columns are percentages still sum to 100%, which a gap would
 * break.
 */
const bandSx = (columnGap: number | string, columnMinWidth?: number | string) =>
  ({
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    minWidth: 0,
    // Clips each cell's column line (below) to this band's own height.
    overflow: 'hidden',
    '& > :only-child': {
      display: 'contents',
    },
    '& > *, & > :only-child > *': {
      // Flex items default to `min-width: auto`, which lets one long unbroken
      // value widen its column past its share and pull the row out of step
      // with the header.
      minWidth: columnMinWidth ?? 0,
      paddingInline: columnGap,
      position: 'relative',
    },
    // Column lines, matching DataTable's grid. Cells are only as tall as their
    // content, so the line is a pseudo-element run far past the cell and cut
    // off by the band's overflow: that makes it exactly as tall as the row,
    // without changing how any page lays out the inside of its cells.
    '& > *:not(:last-child)::after, & > :only-child > *:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: '-100vh',
      bottom: '-100vh',
      right: 0,
      width: '1px',
      bg: TABLE_GRID_COLOR,
      pointerEvents: 'none',
    },
  } as const);

/** Every cell, whether it is a band's direct child or inside a dissolved wrapper. */
const CELLS = '& > *, & > :only-child > *';

/**
 * The header band: the same cell rules, plus centered text, whatever the
 * cells' own alignment. Merged under the same selector rather than spread
 * beside it, which would replace the cells' padding and positioning and
 * pull the header out of line with the rows.
 */
const headerBandSx = (band: ReturnType<typeof bandSx>) =>
  ({
    ...band,
    [CELLS]: {
      ...band[CELLS],
      textAlign: 'center',
      justifyContent: 'center',
    },
  } as const);

const CustomTableView = <T extends object>(props: CustomTableProps<T>) => {
  const theme = useTheme();

  const {
    data,
    mt = 2,
    isLoading,
    maxH = '52vh',
    minW,
    columnGap = '12px',
    columnMinWidth,
    pagination,
    fixedHeaderRow = false,
    fixedFooterRow = false,
    children: {
      renderRow,
      renderEmpty = () => null,
      headerRow,
      footerRow,
      subHeaderRow,
    },
  } = props;

  const pc = theme?.colors?.primary['800'];

  const band = bandSx(columnGap, columnMinWidth);

  // Rows sit flush, divided by grid lines, the way DataTable's do. The cells
  // carry the horizontal padding so the first and last columns line up with
  // the frame.
  const rowProps = {
    py: 4,
    sx: band,
    borderBottom: '1px solid',
    borderBottomColor: TABLE_GRID_COLOR,
  };

  const bandProps = {
    ...rowProps,
    bg: `${pc}80`,
    color: 'support.500',
    userSelect: 'none' as const,
    fontWeight: 'bold',
  };

  return (
    <Flex direction="column" pos="relative" mt={mt}>
      {/* Owns horizontal scrolling. `minWidth: 0` is what keeps that scrolling
          here rather than pushing the whole page sideways: as a flex item this
          box's automatic minimum is its content's width, so without it the
          overflow escapes upward until some ancestor absorbs it. */}
      <Box
        w="100%"
        minW={0}
        overflowX="auto"
        overflowY="hidden"
        border="1px solid"
        borderColor={TABLE_FRAME_COLOR}
        borderRadius="8px"
        sx={scrollbarSx}>
        {/* The width the table insists on. Expressed as a minimum rather than
            `max-content`, which would feed back into the ancestors' sizing and
            stretch the page instead of overflowing this box. */}
        <Box display="grid" minWidth={minW || '100%'} fontSize="13px">
          <Box
            {...bandProps}
            sx={headerBandSx(band)}
            {...(fixedHeaderRow && {
              position: 'sticky',
              top: '0',
              zIndex: 1,
              backdropFilter: 'blur(15px)',
            })}>
            {headerRow}
          </Box>
          {/* Owns vertical scrolling, so the header above stays put while the
              rows move. `overflow-x: clip` rather than `hidden` because
              `hidden` would make this a horizontal scroll container too and
              take the scrollbar away from the box above. */}
          <Box
            maxH={maxH}
            overflowY="auto"
            overflowX="clip"
            sx={{
              ...scrollbarSx,
              // The last row leaves its bottom edge to the frame.
              '& > :last-child': { borderBottom: 'none' },
            }}>
            {isLoading ? (
              <Center py={20} px={5}>
                <CircularProgress isIndeterminate color="primary.400" />
              </Center>
            ) : data.length > 0 ? (
              <>
                {subHeaderRow && <Box {...bandProps}>{subHeaderRow}</Box>}
                {data.map((item: any, index: number) => (
                  <Box
                    key={`${JSON.stringify(item)}-${index}`}
                    {...rowProps}
                    cursor="pointer"
                    color="support.500"
                    bg={(index + 1) % 2 === 0 ? `${pc}30` : undefined}
                    _hover={{
                      bg: 'primary.800',
                      transition: '0.3s',
                    }}>
                    {renderRow(item)}
                  </Box>
                ))}
              </>
            ) : (
              renderEmpty?.()
            )}
          </Box>
          {footerRow && data.length > 0 && !isLoading && (
            <Box
              {...bandProps}
              borderBottom="none"
              borderTop="1px solid"
              borderTopColor={TABLE_GRID_COLOR}
              {...(fixedFooterRow && {
                position: 'sticky',
                bottom: '0',
                zIndex: 1,
                backdropFilter: 'blur(15px)',
              })}>
              {footerRow}
            </Box>
          )}
        </Box>
      </Box>
      {pagination && pagination.totalCount > LIMITS_PER_PAGE && (
        <Flex mt={10} align="center" justifyContent="flex-end">
          <IconButton
            aria-label="next-page"
            icon={<GrPrevious />}
            isDisabled={pagination.page === 1}
            onClick={() => {
              pagination.setPage((old: number) => (data ? old - 1 : old));
            }}
          />
          <Text mx={6}>
            {pagination.page} out of {Math.ceil(pagination.totalPages)}
          </Text>
          <IconButton
            aria-label="next-page"
            icon={<GrNext />}
            isDisabled={
              data.length > 0 ? pagination.totalPages <= pagination.page : true
            }
            onClick={() =>
              pagination.setPage((old: number) => (data ? old + 1 : old))
            }
          />
        </Flex>
      )}
    </Flex>
  );
};

export default CustomTableView;
